import React, { useState, useEffect, useRef, useMemo } from 'react';
import APP_COLOURS from '../../config/colors';
import moment from 'moment';
import { getAvatarImage } from '../../config/avatars';
import { managerAPI } from '../../config/api';
import QRCode from 'react-qr-code';

const LiveTracker = ({ staff, onStaffClick }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [generatingQR, setGeneratingQR] = useState(false);
  const hasFetchedRef = useRef(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Track screen width for responsive layout
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Fetch today's snapshot once when component mounts
    console.log('🔴 LiveTracker useEffect fired!');
    console.log('🔴 About to call fetchTodaySnapshot...');
    fetchTodaySnapshot();
  }, []);

  const fetchTodaySnapshot = async () => {
    try {
      console.log('🟢 fetchTodaySnapshot STARTED');
      setLoading(true);
      console.log('🟢 Loading set to true');
      console.log('🟢 About to call managerAPI.getTodaySnapshot()...');
      
      const response = await managerAPI.getTodaySnapshot();
      
      console.log('🟢 API call completed!');
      console.log('=== RAW API RESPONSE ===');
      console.log('Full response:', response);
      console.log('Records count:', response?.count);
      console.log('Records array:', response?.records);
      
      // Log first record to see structure
      if (response?.records && response.records.length > 0) {
        console.log('Sample record structure:', response.records[0]);
        console.log('Sample record keys:', Object.keys(response.records[0]));
      } else {
        console.log('⚠️ No records found in response');
      }
      
      // Store the raw response
      console.log('🟢 Setting todayData...');
      setTodayData(response);
      setLastRefresh(new Date());
      hasFetchedRef.current = true;
      console.log('🟢 fetchTodaySnapshot COMPLETED successfully');
    } catch (error) {
      console.error('🔴 ERROR in fetchTodaySnapshot:', error);
      console.error('🔴 Error message:', error.message);
      console.error('🔴 Error stack:', error.stack);
    } finally {
      console.log('🟢 Setting loading to false');
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    try {
      setGeneratingQR(true);
      console.log('Generating leaderboard QR code...');
      
      const data = await managerAPI.generateLeaderboardToken();
      
      console.log('Leaderboard token generated:', data);
      
      // Create the full URL for the leaderboard
      const fullUrl = `${window.location.origin}/leaderboard/${data.token}`;
      
      setQrData({
        url: fullUrl,
        token: data.token,
        expiresAt: data.expiresAt
      });
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code. Please try again.');
    } finally {
      setGeneratingQR(false);
    }
  };

  // Resolve trs from multiple possible locations on an attendance record
  const resolveTrs = (record) => {
    if (record.trs !== undefined && parseFloat(record.trs) > 0) return parseFloat(record.trs);
    if (record.totalSales !== undefined && parseFloat(record.totalSales) > 0) return parseFloat(record.totalSales);
    if (Array.isArray(record.categoryPerformance) && record.categoryPerformance.length > 0) {
      return record.categoryPerformance.reduce((sum, cat) => sum + (parseFloat(cat.total) || 0), 0);
    }
    return 0;
  };

  // Process attendance records directly - use the embedded user_data
  const onlineStaff = useMemo(() => {
    if (!todayData || !todayData.records || !Array.isArray(todayData.records)) {
      console.log('No attendance records available');
      return [];
    }
    
    console.log('📊 Processing attendance records:', todayData.records.length);
    
    // Map attendance records - data is already embedded!
    const staffWithSales = todayData.records.map((record) => {
      // Names/avatar can sit directly on the record OR nested inside user_data
      const userData = record.user_data || {};
      const firstName = record.first_name || userData.first_name || userData.name || 'Unknown';
      const lastName = record.last_name || userData.last_name || '';
      const avatar = (record.avatar || userData.avatar)?.toString() || '1';
      const position = record.position || userData.position || 'Staff';

      // staff identifier may be staff_id or user_id
      const staffId = record.staff_id || record.user_id;
      
      const totalSales = resolveTrs(record);
      const totalTransactions = (record.salesCount || 0) || ((record.existCustomer || 0) + (record.newCustomer || 0));
      
      return {
        id: staffId,
        first_name: firstName,
        last_name: lastName,
        avatar,
        position,
        last_seen: record.last_seen,
        todayAttendance: {
          clockIn: record.checkin || record.clockIn,
          clockOut: record.checkout || record.clockOut,
          lunchStart: record.lunch_start,
          lunchEnd: record.lunch_end
        },
        todaySales: { totalSales, totalTransactions }
      };
    });

    console.log('✅ Processed staff count:', staffWithSales.length);
    if (staffWithSales.length > 0) {
      console.log('📸 Sample staff avatar:', staffWithSales[0].avatar, 'Name:', staffWithSales[0].first_name);
    }

    // Sort by total sales (highest first) - LEADERBOARD
    return staffWithSales.sort((a, b) => {
      const aSales = a.todaySales?.totalSales || 0;
      const bSales = b.todaySales?.totalSales || 0;
      return bSales - aSales; // Descending order
    });
  }, [todayData]);

  // Calculate summary totals
  const summaryTotals = useMemo(() => {
    if (!todayData?.records) return { totalSales: 0, totalTransactions: 0 };
    
    return todayData.records.reduce((acc, record) => {
      acc.totalSales += resolveTrs(record);
      acc.totalTransactions += (record.salesCount || 0) || ((record.existCustomer || 0) + (record.newCustomer || 0));
      return acc;
    }, { totalSales: 0, totalTransactions: 0 });
  }, [todayData]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return 'R ' + parseFloat(amount || 0).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  };

  const formatAttendanceTime = (timeData) => {
    if (!timeData) return '-';
    
    let timeValue;
    if (timeData.toDate) {
      timeValue = moment(timeData.toDate());
    } else if (typeof timeData === 'string') {
      timeValue = moment(timeData);
    } else if (timeData._seconds) {
      timeValue = moment.unix(timeData._seconds);
    } else {
      return '-';
    }
    
    return timeValue.format('HH:mm');
  };

  const isMobile = screenWidth < 768;

  return (
    <div
      className="rounded-2xl h-full w-full"
      style={{
        backgroundColor: APP_COLOURS.CARD,
        border: `1px solid ${APP_COLOURS.BLUE}20`,
        padding: isMobile ? '12px' : '20px',
      }}
    >
      {/* Header */}
      <div className={`flex items-center justify-between ${isMobile ? 'mb-2' : 'mb-2'}`}>
        <div>
          <h2 className={`font-bold ${isMobile ? 'text-base' : 'text-lg'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
         Live Tracker
          </h2>
          <p className={`opacity-70 mt-0.5 ${isMobile ? 'text-[9px]' : 'text-[10px]'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
            {onlineStaff.length} checked in • {lastRefresh ? moment(lastRefresh).format('HH:mm') : '--:--'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isMobile && (
          <button
            onClick={generateQRCode}
            disabled={generatingQR}
            className="px-3 py-1.5 rounded text-[11px] font-medium transition-all duration-200 flex items-center gap-1.5"
            style={{ 
              backgroundColor: APP_COLOURS.BLUE, 
              color: '#ffffff',
              opacity: generatingQR ? 0.6 : 1,
              cursor: generatingQR ? 'not-allowed' : 'pointer',
              border: 'none'
            }}
            onMouseEnter={(e) => !generatingQR && (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={(e) => !generatingQR && (e.currentTarget.style.opacity = '1')}
          >
            <i className={`fas fa-qrcode ${generatingQR ? 'animate-pulse' : ''}`}></i>
            {generatingQR ? 'Generating...' : 'QR Code'}
          </button>
          )}
          <div className="text-right">
            <div className={`font-bold tabular-nums ${isMobile ? 'text-base' : 'text-xl'}`} style={{ color: APP_COLOURS.BLUE }}>
              {formatTime(currentTime)}
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Button & Summary */}
      <div className={`flex items-center justify-between ${isMobile ? 'mb-2 gap-2' : 'mb-2'}`}>
        <div className="flex items-center gap-2">
        <button
          onClick={() => {
            hasFetchedRef.current = false;
            fetchTodaySnapshot();
          }}
          disabled={loading}
            className={`rounded font-medium transition-all duration-200 ${isMobile ? 'px-2 py-1 text-[9px]' : 'px-2.5 py-1 text-[10px]'}`}
          style={{ 
            backgroundColor: APP_COLOURS.BLUE, 
            color: '#ffffff',
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
          onMouseEnter={(e) => !loading && (e.currentTarget.style.opacity = '0.8')}
          onMouseLeave={(e) => !loading && (e.currentTarget.style.opacity = '1')}
        >
            <i className={`fas fa-sync-alt ${loading ? 'animate-spin' : ''}`} style={{ marginRight: isMobile ? '4px' : '4px' }}></i>
            {isMobile ? '' : 'Refresh'}
          </button>
          {isMobile && (
            <button
              onClick={generateQRCode}
              disabled={generatingQR}
              className="px-2 py-1 rounded text-[9px] font-medium transition-all duration-200 flex items-center"
              style={{ 
                backgroundColor: APP_COLOURS.BLUE, 
                color: '#ffffff',
                opacity: generatingQR ? 0.6 : 1,
                cursor: generatingQR ? 'not-allowed' : 'pointer',
                border: 'none'
              }}
            >
              <i className={`fas fa-qrcode ${generatingQR ? 'animate-pulse' : ''}`}></i>
        </button>
          )}
        </div>
        {todayData && todayData.records && (
          <div className={`flex items-center gap-2 ${isMobile ? 'text-[9px]' : 'text-[10px]'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
            <div className="flex items-center gap-1">
              <i className="fas fa-coins" style={{ color: APP_COLOURS.GREEN, fontSize: isMobile ? '9px' : '10px' }}></i>
              <span className="font-bold" style={{ color: APP_COLOURS.GREEN }}>
                {isMobile ? formatCurrency(summaryTotals.totalSales).replace('R ', 'R') : formatCurrency(summaryTotals.totalSales)}
              </span>
            </div>
            {!isMobile && (
            <div className="flex items-center gap-1">
              <i className="fas fa-shopping-cart" style={{ color: APP_COLOURS.BLUE }}></i>
              <span>{summaryTotals.totalTransactions} sales</span>
            </div>
            )}
          </div>
        )}
      </div>

      {/* Leaderboard Content */}
      <div className="overflow-y-auto custom-scrollbar" style={{ maxHeight: isMobile ? '400px' : '600px' }}>
        {loading ? (
          <div className="text-center py-6">
            <div className={`animate-spin rounded-full border-b-2 mx-auto mb-2 ${isMobile ? 'h-8 w-8' : 'h-10 w-10'}`} style={{ borderColor: APP_COLOURS.BLUE }}></div>
            <p className={`${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading leaderboard...</p>
          </div>
        ) : onlineStaff.length === 0 ? (
          <div className="text-center py-6">
            <div
              className={`rounded-full flex items-center justify-center mx-auto mb-2 ${isMobile ? 'w-10 h-10' : 'w-12 h-12'}`}
              style={{ backgroundColor: `${APP_COLOURS.BLUE}10` }}
            >
              <i className={`fas fa-user-clock ${isMobile ? 'text-base' : 'text-xl'}`} style={{ color: APP_COLOURS.BLUE, opacity: 0.5 }}></i>
            </div>
            <p className={`opacity-70 ${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
              No staff checked in today
            </p>
          </div>
        ) : isMobile ? (
          /* MOBILE: Card-based compact layout */
          <div className="space-y-2">
            {onlineStaff.map((member, idx) => {
              const rank = idx + 1;
              const sales = member.todaySales?.totalSales || 0;
              const transactions = member.todaySales?.totalTransactions || 0;
              const isTopThree = rank <= 3;
              const isLeader = rank === 1;
              
              const isSeenToday = member.last_seen && member.last_seen._seconds 
                ? moment.unix(member.last_seen._seconds).isSame(moment(), 'day')
                : false;
              const statusColor = isSeenToday ? APP_COLOURS.SUCCESS : APP_COLOURS.WARNING || APP_COLOURS.ORANGE;

              return (
                <div
                  key={member.id || idx}
                  className="rounded-lg p-2.5 transition-all duration-200"
                  style={{
                    backgroundColor: isTopThree ? `${APP_COLOURS.BLUE}08` : APP_COLOURS.BG3,
                    border: isLeader ? `1px solid ${APP_COLOURS.BLUE}40` : `1px solid ${APP_COLOURS.BG4}30`,
                  }}
                  onClick={() => {
                    const fullStaffMember = staff?.find(s => s.id === member.id);
                    if (fullStaffMember && onStaffClick) {
                      onStaffClick(fullStaffMember);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {/* Rank Badge */}
                    <div className="flex-shrink-0 w-6 text-center">
                      {isLeader ? (
                        <span className="text-sm">🥇</span>
                      ) : rank === 2 ? (
                        <span className="text-sm">🥈</span>
                      ) : rank === 3 ? (
                        <span className="text-sm">🥉</span>
                      ) : (
                        <span className="text-[10px] font-bold" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.4 }}>
                          {rank}
                        </span>
                      )}
                    </div>

                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <img
                        src={getAvatarImage(member.avatar || '1')}
                        alt={`${member.first_name}`}
                        className="w-8 h-8 rounded-full object-cover"
                        style={{
                          border: isTopThree ? `2px solid ${APP_COLOURS.BLUE}` : `1.5px solid ${APP_COLOURS.BG4}`,
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.nextSibling;
                          if (fallback && fallback.classList.contains('avatar-fallback')) {
                            fallback.style.display = 'flex';
                          }
                        }}
                      />
                      <div
                        className="avatar-fallback w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[10px] absolute top-0 left-0"
                        style={{
                          background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
                          border: isTopThree ? `2px solid ${APP_COLOURS.BLUE}` : `1.5px solid ${APP_COLOURS.BG4}`,
                          display: 'none',
                        }}
                      >
                        {(member.first_name?.[0] || '') + (member.last_name?.[0] || '')}
                      </div>
                      <div
                        className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: statusColor,
                          border: `1px solid ${APP_COLOURS.CARD}`,
                        }}
                      ></div>
                    </div>

                    {/* Name & Time Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[11px] truncate" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {member.first_name} {member.last_name?.charAt(0)}.
                      </p>
                      <div className="flex items-center gap-2 text-[9px]" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.6 }}>
                        {member.todayAttendance?.clockIn && (
                          <span style={{ color: APP_COLOURS.SUCCESS }}>
                            <i className="fas fa-sign-in-alt mr-0.5"></i>
                            {formatAttendanceTime(member.todayAttendance.clockIn)}
                          </span>
                        )}
                        {member.todayAttendance?.clockOut && (
                          <span>
                            <i className="fas fa-sign-out-alt mr-0.5"></i>
                            {formatAttendanceTime(member.todayAttendance.clockOut)}
                          </span>
                        )}
                        {!member.todayAttendance?.clockIn && (
                          <span style={{ fontStyle: 'italic' }}>
                            {member.last_seen && member.last_seen._seconds
                              ? moment.unix(member.last_seen._seconds).fromNow()
                              : 'No data'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sales Amount */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-xs" style={{ 
                        color: sales > 0 ? APP_COLOURS.GREEN : APP_COLOURS.TEXTCOLOR,
                        opacity: sales > 0 ? 1 : 0.3,
                      }}>
                        {formatCurrency(sales).replace('R ', 'R')}
                      </p>
                      {transactions > 0 && (
                        <p className="text-[8px]" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.5 }}>
                          {transactions} sale{transactions !== 1 ? 's' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* DESKTOP: Table layout */
          <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
            <thead>
              <tr style={{ 
                backgroundColor: `${APP_COLOURS.BG3}`,
                borderBottom: `2px solid ${APP_COLOURS.BG4}`
              }}>
                <th className="text-left py-2 px-2 text-[10px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR, width: '40px' }}>
                  #
                </th>
                <th className="text-left py-2 px-2 text-[10px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Staff
                </th>
                <th className="text-center py-2 px-2 text-[10px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR, width: '80px' }}>
                  <i className="fas fa-eye mr-1"></i>
                  Last Seen
                </th>
                <th className="text-center py-2 px-2 text-[10px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR, width: '70px' }}>
                  <i className="fas fa-clock mr-1"></i>
                  Check-in
                </th>
                <th className="text-center py-2 px-2 text-[10px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR, width: '90px' }}>
                  <i className="fas fa-utensils mr-1"></i>
                  Lunch
                </th>
                <th className="text-center py-2 px-2 text-[10px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR, width: '70px' }}>
                  <i className="fas fa-door-open mr-1"></i>
                  Check-out
                </th>
                <th className="text-right py-2 px-2 text-[10px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR, width: '150px' }}>
                  Total Sales
                </th>
              </tr>
            </thead>
            <tbody>
              {onlineStaff.map((member, idx) => {
                const rank = idx + 1;
                const sales = member.todaySales?.totalSales || 0;
                const transactions = member.todaySales?.totalTransactions || 0;
                const isTopThree = rank <= 3;
                const isLeader = rank === 1;
            
                // Check if last seen was today
                const isSeenToday = member.last_seen && member.last_seen._seconds 
                  ? moment.unix(member.last_seen._seconds).isSame(moment(), 'day')
                  : false;
                
                // Determine status color: green if seen today, orange/yellow if not
                const statusColor = isSeenToday ? APP_COLOURS.SUCCESS : APP_COLOURS.WARNING || APP_COLOURS.ORANGE;
            
            return (
                  <tr
              key={member.id || idx}
                    className="transition-all duration-200 hover:shadow-md cursor-pointer"
              style={{
                      backgroundColor: idx % 2 === 0 ? APP_COLOURS.BG3 : 'transparent',
                      borderBottom: `1px solid ${APP_COLOURS.BG4}20`,
                    }}
                    onClick={() => {
                      // Find the full staff object from the staff prop
                      const fullStaffMember = staff?.find(s => s.id === member.id);
                      if (fullStaffMember && onStaffClick) {
                        onStaffClick(fullStaffMember);
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${APP_COLOURS.BLUE}15`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = idx % 2 === 0 ? APP_COLOURS.BG3 : 'transparent';
                    }}
                  >
                    {/* Rank */}
                    <td className="py-2 px-2 text-center">
                      {isLeader ? (
                        <span className="text-lg">🥇</span>
                      ) : rank === 2 ? (
                        <span className="text-lg">🥈</span>
                      ) : rank === 3 ? (
                        <span className="text-lg">🥉</span>
                      ) : (
                        <span className="text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.5 }}>
                          {rank}
                        </span>
                      )}
                    </td>

                    {/* Staff Info */}
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-2">
              {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={getAvatarImage(member.avatar || '1')}
                      alt={`${member.first_name} ${member.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                      style={{
                              border: isTopThree ? `2px solid ${APP_COLOURS.BLUE}` : `2px solid ${APP_COLOURS.BG4}`,
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextSibling;
                        if (fallback && fallback.classList.contains('avatar-fallback')) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />
                          {/* Fallback */}
                    <div
                            className="avatar-fallback w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs absolute top-0 left-0"
                  style={{
                    background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
                              border: isTopThree ? `2px solid ${APP_COLOURS.BLUE}` : `2px solid ${APP_COLOURS.BG4}`,
                        display: 'none',
                  }}
                >
                  {(member.first_name?.[0] || '') + (member.last_name?.[0] || '')}
                </div>
                          {/* Online Indicator */}
                <div
                            className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full animate-pulse"
                  style={{
                    backgroundColor: statusColor,
                              border: `1.5px solid ${APP_COLOURS.BG}`,
                  }}
                ></div>
              </div>

                        {/* Name & Position */}
                  <div className="flex-1 min-w-0">
                          <p className="font-semibold text-xs truncate" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {member.first_name} {member.last_name}
                          </p>
                          <p className="text-[9px] opacity-60 truncate" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {member.position || 'Staff'} • {member.last_seen && member.last_seen._seconds
                              ? moment.unix(member.last_seen._seconds).fromNow()
                              : 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Last Seen */}
                    <td className="py-2 px-2 text-center">
                      <div>
                        <p className="text-[8px] font-light" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                          Last seen
                        </p>
                        <p className="text-[10px] font-semibold" style={{ 
                          color: APP_COLOURS.TEXTCOLOR,
                          fontStyle: member.last_seen ? 'normal' : 'italic'
                        }}>
                          {member.last_seen && member.last_seen._seconds
                            ? moment.unix(member.last_seen._seconds).fromNow()
                            : 'unavailable'}
                        </p>
                      </div>
                    </td>

                    {/* Clock In Time */}
                    <td className="py-2 px-2 text-center">
                      {member.todayAttendance?.clockIn ? (
                        <div>
                          <p className="text-[8px] font-light" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                            Check-in
                          </p>
                          <span className="text-[11px] font-semibold" style={{ color: APP_COLOURS.SUCCESS }}>
                            {formatAttendanceTime(member.todayAttendance.clockIn)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[10px]" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.3 }}>
                          -
                        </span>
                      )}
                    </td>

                    {/* Lunch Time */}
                    <td className="py-2 px-2 text-center" style={{ opacity: member.todayAttendance?.lunchStart ? 1 : 0.3 }}>
                      {member.todayAttendance?.lunchStart ? (
                        <div className="flex flex-col items-center">
                          <p className="text-[8px] font-light" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                            Lunch
                          </p>
                          <span className="text-[10px] font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {formatAttendanceTime(member.todayAttendance.lunchStart)}
                            {' - '}
                            {member.todayAttendance?.lunchEnd ? formatAttendanceTime(member.todayAttendance.lunchEnd) : '_'}
                          </span>
                        </div>
                      ) : (
                        <div>
                          <p className="text-[8px] font-light" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                            Lunch
                          </p>
                          <span className="text-[10px]" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            _ - _
                          </span>
                        </div>
                      )}
                    </td>

                    {/* Clock Out Time */}
                    <td className="py-2 px-2 text-center" style={{ opacity: member.todayAttendance?.clockOut ? 1 : 0.3 }}>
                      {member.todayAttendance?.clockOut ? (
                        <div>
                          <p className="text-[8px] font-light" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                            Check-out
                          </p>
                          <span className="text-[11px] font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {formatAttendanceTime(member.todayAttendance.clockOut)}
                          </span>
                        </div>
                      ) : (
                        <div>
                          <p className="text-[8px] font-light" style={{ color: APP_COLOURS.TEXTCOLOR, opacity: 0.8 }}>
                            Check-out
                          </p>
                          <span className="text-[10px]" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            _
                          </span>
                        </div>
                      )}
                    </td>

                    {/* Sales */}
                    <td className="py-2 px-2 text-right">
                      <div>
                        <p className="font-bold text-sm" style={{ 
                          color: sales > 0 ? APP_COLOURS.GREEN : APP_COLOURS.TEXTCOLOR,
                          opacity: sales > 0 ? 1 : 0.3,
                          margin: 0
                        }}>
                          {formatCurrency(sales)}
                        </p>
                        {transactions > 0 && (
                          <p className="text-[9px] mt-1" style={{ 
                            color: APP_COLOURS.TEXTCOLOR, 
                            opacity: 0.6,
                            margin: '2px 0 0 0'
                          }}>
                            {transactions} {transactions === 1 ? 'transaction' : 'transactions'}
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && qrData && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={() => setShowQRModal(false)}
        >
          <div
            className={`rounded-2xl w-full mx-4 ${isMobile ? 'p-4 max-w-sm' : 'p-8 max-w-lg'}`}
            style={{
              backgroundColor: APP_COLOURS.CARD,
              border: `1px solid ${APP_COLOURS.BLUE}30`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex justify-between items-start ${isMobile ? 'mb-3' : 'mb-6'}`}>
              <div>
                <h3 className={`font-bold mb-1 ${isMobile ? 'text-base' : 'text-xl'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Leaderboard QR Code
                </h3>
                <p className={`opacity-70 ${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Scan to view live leaderboard
                </p>
              </div>
              <button
                onClick={() => setShowQRModal(false)}
                className={isMobile ? 'text-xl' : 'text-2xl'}
                style={{ 
                  color: APP_COLOURS.TEXTCOLOR, 
                  opacity: 0.5,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <div
              className={`rounded-xl flex justify-center ${isMobile ? 'p-4 mb-3' : 'p-6 mb-4'}`}
              style={{ backgroundColor: '#ffffff' }}
            >
              <QRCode
                value={qrData.url}
                size={isMobile ? 180 : 256}
                level="H"
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
            </div>

            <div
              className={`rounded-lg ${isMobile ? 'p-2.5 mb-3' : 'p-4 mb-4'}`}
              style={{ backgroundColor: `${APP_COLOURS.BLUE}10` }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <i className={`fas fa-link ${isMobile ? 'text-xs' : 'text-sm'}`} style={{ color: APP_COLOURS.BLUE }}></i>
                <span className={`font-semibold ${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Direct Link
                </span>
              </div>
              <p
                className={`break-all font-mono rounded ${isMobile ? 'text-[9px] p-1.5' : 'text-xs p-2'}`}
                style={{
                  color: APP_COLOURS.TEXTCOLOR,
                  backgroundColor: APP_COLOURS.BG3,
                  wordBreak: 'break-all'
                }}
              >
                {qrData.url}
              </p>
            </div>

            <div
              className={`rounded-lg ${isMobile ? 'p-2 mb-3' : 'p-3 mb-4'}`}
              style={{ backgroundColor: `${APP_COLOURS.WARNING}10` }}
            >
              <div className="flex items-center gap-2">
                <i className={`fas fa-clock ${isMobile ? 'text-xs' : 'text-sm'}`} style={{ color: APP_COLOURS.WARNING }}></i>
                <span className={isMobile ? 'text-[10px]' : 'text-xs'} style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Expires: {moment(qrData.expiresAt).format(isMobile ? 'MMM Do, h:mm A' : 'MMMM Do, YYYY [at] h:mm A')}
                </span>
              </div>
            </div>

            <div className={`flex ${isMobile ? 'gap-2' : 'gap-3'}`}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(qrData.url);
                  alert('Link copied to clipboard!');
                }}
                className={`flex-1 rounded-lg font-medium transition-all duration-200 ${isMobile ? 'px-2 py-2 text-xs' : 'px-4 py-2 text-sm'}`}
                style={{
                  backgroundColor: APP_COLOURS.BLUE,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <i className={`fas fa-copy ${isMobile ? 'mr-1' : 'mr-2'}`}></i>
                Copy
              </button>
              <button
                onClick={() => window.open(qrData.url, '_blank')}
                className={`flex-1 rounded-lg font-medium transition-all duration-200 ${isMobile ? 'px-2 py-2 text-xs' : 'px-4 py-2 text-sm'}`}
                style={{
                  backgroundColor: APP_COLOURS.SUCCESS,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <i className={`fas fa-external-link-alt ${isMobile ? 'mr-1' : 'mr-2'}`}></i>
                Open
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTracker;

